/** @format — Map API / AG-UI payloads → tracking UI models */

import { buildRoutePaths, toLatLng } from "./routeGeometry";
import { getShipmentStatusLabel } from "@/utils/shipmentStatus";

export function latLng(point) {
  return toLatLng(point);
}

function formatLatLon(pos) {
  if (!pos || pos.lat == null || pos.lng == null) return null;
  const latDir = pos.lat >= 0 ? "N" : "S";
  const lngDir = pos.lng >= 0 ? "E" : "W";
  return `${Math.abs(pos.lat).toFixed(1)}°${latDir}, ${Math.abs(pos.lng).toFixed(1)}°${lngDir}`;
}

export const DEFAULT_SPEED_COURSE = "0kn / 0°";
export const DEFAULT_ETA_LABEL = "0";

function formatSpeedCourse(pos) {
  if (!pos) return DEFAULT_SPEED_COURSE;
  const sog = pos.sog != null ? `${pos.sog}kn` : "0kn";
  const cog = pos.cog != null ? `${pos.cog}°` : "0°";
  return `${sog} / ${cog}`;
}

function formatEta(etaDate) {
  // Guard against an eta object slipping through (e.g. {value, source}); only a
  // scalar date string is renderable. Prevents "[object Object]" (#70).
  if (!etaDate || typeof etaDate === "object") return DEFAULT_ETA_LABEL;
  try {
    const d = new Date(etaDate);
    if (Number.isNaN(d.getTime())) return String(etaDate);
    const now = new Date();
    const diffMs = d.getTime() - now.getTime();
    const days = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
    if (days === 0) return "0 days";
    return days === 1 ? "1 day" : `${days} days`;
  } catch {
    return DEFAULT_ETA_LABEL;
  }
}

function formatEtaDateTime(etaDate) {
  if (!etaDate || typeof etaDate === "object") return DEFAULT_ETA_LABEL;
  try {
    const d = new Date(etaDate);
    if (Number.isNaN(d.getTime())) return String(etaDate);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return DEFAULT_ETA_LABEL;
  }
}

/** Resolve code for POST /v1/agents/tracking/stream/{tracking_code} */
export function resolveTrackingCode(row) {
  if (!row) return "";
  return row.bookingId || String(row.shipmentRef ?? "").replace(/^#/, "") || row.trackingCode || row.shipmentId || "";
}

/** GET /v1/shipments/active row → list + map marker */
export function mapActiveShipmentRow(row) {
  const pin = row.mapPin;
  const current = pin?.currentPosition;
  const origin = pin?.origin;
  const destination = pin?.destination;
  const vesselPos = latLng(current);
  const progressPercent = row.progressPercent ?? 0;
  const routePaths = buildRoutePaths({
    routePoints: pin?.routePoints ?? [],
    origin,
    destination,
    currentPosition: current,
    progressPercent,
  });

  return {
    id: row.shipmentId,
    shipmentId: row.shipmentId,
    title: row.vehicle ?? "Shipment",
    routeLabel: [row.originLabel, row.destinationLabel].filter(Boolean).join(" to "),
    routeProgressPercent: progressPercent,
    displayId: row.shipmentRef || row.bookingId || row.shipmentId,
    bookingId: row.bookingId,
    trackingCode: resolveTrackingCode(row),
    status: row.status,
    origin: row.originLabel,
    destination: row.destinationLabel,
    vesselPosition: vesselPos,
    latLonLabel: formatLatLon(current) ?? null,
    speedCourse: formatSpeedCourse(current),
    etaDateTime: DEFAULT_ETA_LABEL,
    vessel: row.vesselName ?? null,
    routeStart: routePaths.routeStart,
    routeEnd: routePaths.routeEnd,
    routeProgressEnd: routePaths.routeProgressEnd,
    routeCompletedPath: routePaths.routeCompletedPath,
    routeRemainingPath: routePaths.routeRemainingPath,
    hasMapPin: Boolean(pin && (current || origin || destination)),
  };
}

/** GET /v1/shipments/tracked-history row → history list item */
export function mapHistoryRow(row, index = 0) {
  const viewedKey = row.lastViewedAt ?? String(index);
  return {
    id: `${row.shipmentId}-${viewedKey}`,
    shipmentId: row.shipmentId,
    label: row.vehicle || row.bookingId || row.shipmentId,
    routeLabel: [row.originLabel, row.destinationLabel].filter(Boolean).join(" to "),
    timestampLabel: row.lastViewedLabel ?? "",
    trackingCode: resolveTrackingCode(row),
    displayId: row.shipmentRef || row.bookingId,
    routeProgressPercent: row.progressPercent,
  };
}

/** tracking.map / tracking.summary AG-UI props → ProgressCard + map model */
export function mapTrackingStreamPropsToShipment(props = {}, shipmentId = null) {
  const tracking = props.tracking ?? {};
  const map = tracking.map ?? props.map ?? {};
  const origin = map.origin ?? props.origin;
  const destination = map.destination ?? props.destination;
  const current = map.currentPosition ?? tracking.position?.current ?? props.currentPosition;
  const routePoints = map.routePoints ?? [];
  const vessel = tracking.vessel ?? {};
  const progress = tracking.progress ?? props.progress ?? {};

  const pct = progress.percent ?? props.progressPercent ?? tracking.progressPercent ?? 0;

  const routePaths = buildRoutePaths({
    routePoints,
    origin,
    destination,
    currentPosition: current,
    progressPercent: pct,
  });

  return {
    id: shipmentId ?? props.shipmentRef ?? props.bookingRef ?? "stream",
    displayId: props.shipmentRef ?? props.bookingRef ?? props.bookingId,
    title: props.vehicle ?? "Shipment",
    origin: typeof origin === "string" ? origin : origin?.name,
    destination: typeof destination === "string" ? destination : destination?.name,
    routeProgressPercent: pct,
    vessel: vessel.name ?? props.vesselName ?? props.vessel,
    vesselPosition: latLng(current),
    routeStart: routePaths.routeStart,
    routeEnd: routePaths.routeEnd,
    routeProgressEnd: routePaths.routeProgressEnd,
    routeCompletedPath: routePaths.routeCompletedPath,
    routeRemainingPath: routePaths.routeRemainingPath,
    latLonLabel: formatLatLon(current) ?? null,
    speedCourse: formatSpeedCourse(current),
    // `tracking.eta` is an object ({value, source}); fall back to its scalar
    // value, never the object, so the card can't render "[object Object]" (#70).
    etaDateTime: formatEtaDateTime(props.etaDate ?? tracking.eta?.value),
    etaDays: formatEta(props.etaDate ?? tracking.eta?.value),
    statusLabel: formatStatusLabel(props.status ?? tracking.status),
    vin: props.vin,
  };
}

function formatStatusLabel(status) {
  if (!status) return getShipmentStatusLabel("in_transit");
  return getShipmentStatusLabel(status);
}

export function extractMapShipmentFromUiComponents(uiComponents = [], fallbackId = null) {
  const blocks = Array.isArray(uiComponents) ? uiComponents : [];
  const mapBlock = blocks.find((b) => b.name === "tracking.map");
  if (mapBlock?.props) {
    return mapTrackingStreamPropsToShipment(mapBlock.props, fallbackId);
  }
  const summaryBlock = blocks.find((b) => b.name === "tracking.summary");
  if (summaryBlock?.props) {
    return mapTrackingStreamPropsToShipment(summaryBlock.props, fallbackId);
  }
  return null;
}

/** All active rows with map pins for multi-marker map */
export function activeRowsToMapShipments(rows = []) {
  return rows.map(mapActiveShipmentRow).filter((s) => s.vesselPosition && s.hasMapPin !== false);
}

/** Already-mapped list rows from useTrackingLists — do not pass through mapActiveShipmentRow again */
export function filterMapReadyShipments(items = []) {
  return items.filter(
    (s) =>
      Array.isArray(s.vesselPosition) &&
      s.vesselPosition.length === 2 &&
      s.hasMapPin !== false,
  );
}
